/**
 * @file LegalRevisionPreview.tsx
 * @description Текст прежней редакции документа (phase19.16, SB143):
 * окно по «Открыть» в истории редакций — что именно принимали покупатели.
 * На телефоне окно во весь экран.
 */

import { useEffect, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { ListSkeleton } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { getLegalRevision, type LegalRevisionDto } from "../../lib/api-legal";
import { formatLegalDate } from "../../lib/legal/legal-doc-view";
import { legalErrorText } from "../../lib/legal/legal-errors";

interface Props {
  docId: string;
  /** Номер редакции; null — окно закрыто. */
  number: number | null;
  onClose: () => void;
}

export function LegalRevisionPreview({ docId, number, onClose }: Props) {
  const [revision, setRevision] = useState<LegalRevisionDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (number === null) return;
    let cancelled = false;
    setRevision(null);
    setError(null);
    getLegalRevision(docId, number)
      .then((r) => !cancelled && setRevision(r))
      .catch((err: unknown) => !cancelled && setError(legalErrorText(err, "Не удалось загрузить редакцию")));
    return () => {
      cancelled = true;
    };
  }, [docId, number]);

  return (
    <Dialog open={number !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[45rem] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{revision ? `${revision.title} — редакция ${revision.number}` : `Редакция ${number ?? ""}`}</DialogTitle>
          <DialogDescription>
            {revision ? `Опубликована ${formatLegalDate(revision.publishedAt)}${revision.publishedByName ? ` · ${revision.publishedByName}` : ""}` : "Текст редакции"}
          </DialogDescription>
        </DialogHeader>
        {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        {!revision && !error ? <ListSkeleton rows={4} compact /> : null}
        {revision?.contentHtml ? (
          <div
            className="prose prose-sm max-w-none prose-headings:font-semibold prose-a:text-primary"
            // Текст редакции — тот же HTML, что видели покупатели.
            dangerouslySetInnerHTML={{ __html: revision.contentHtml }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
